import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays } from 'date-fns';
import { cn, IconButton } from '@c2fo/liquidity';
import { PencilIcon } from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import c2foComputer from '@/enterprise/assets/c2focomputer.svg?url';
import useEnableAutomations from '@/enterprise/components/useEnableAutomations';
import { buildCustomScheduleSentenceFromFormData } from '@/enterprise/features/offerModal/utils';
import useExchangeRates from '@/shared/data/useExchangeRates';
import convertCurrency from '@/utils/convertCurrency';
import handleNaN from '@/utils/handleNaN';
import useLocaleFormat from '@/utils/useLocaleFormat';
import BlendedRateDisplay from './BlendedRateDisplay';
import { useOfferBuilderContext } from './OfferBuilderContext';
import EditFrequencyModal from './OfferReview/EditFrequencyModal';
import EditScheduleModal from './OfferReview/EditScheduleModal';
import { useRateCalculations } from './useRateCalculations';
import { allMarketsHaveRates, calculateDiscount } from './utils';

const PREFERRED_TERM_DAYS = 60;

interface OfferSummaryStats {
  avgDaysPaidEarly: number;
  customerUuids: Set<string>;
  earliestPayDate: Date | null;
  earliestGoodFundsDate: Date | null;
  eligibleDpeWeightedSum: number;
  eligibleInvoiceAmount: number;
  eligibleInvoiceCount: number;
  eligibleDiscountAmount: number;
  latestPayDate: Date | null;
  latestGoodFundsDate: Date | null;
  payDateRange: string;
  goodFundsDateRange: string;
}

const OfferSummary = ({
  step,
  offerSubmitting = false,
}: {
  step: 'confirmation' | 'customer-select' | 'frequency-select' | 'preferred-upgrade' | 'rate-select' | 'review';
  offerSubmitting?: boolean;
}) => {
  const { t } = useTranslation();
  const enableAutomations = useEnableAutomations();
  const { selectedCurrency } = useCurrencyContext();
  const { data: exchangeRates } = useExchangeRates();
  const {
    state: {
      builderVariant,
      selected,
      schedule,
      draftOfferProperties,
      frequency,
      submissionErrors,
      preferredRateSelected,
    },
  } = useOfferBuilderContext();
  const { calculateEffectiveRates, getEffectiveRate } = useRateCalculations();
  const effectiveRates = calculateEffectiveRates();
  const [showEditScheduleModal, setShowEditScheduleModal] = useState(false);
  const { asCurrency, asDateStringText, asNumber } = useLocaleFormat();
  const [showEditFrequencyModal, setShowEditFrequencyModal] = useState(false);
  const isDraftVariant = builderVariant === 'draft';

  // show all selected on non-confirmation pages
  // filter out failed submissions on confirmation page
  const displayedMarkets = useMemo(() => {
    if (step !== 'confirmation') {
      return selected;
    }

    const failedMarketKeys = new Set(
      submissionErrors
        .map((error) => {
          if (error.payload && 'marketUuid' in error.payload && 'supplierDivisionUuid' in error.payload) {
            return `${error.payload.marketUuid}-${error.payload.supplierDivisionUuid}`;
          }

          return null;
        })
        .filter((key): key is string => key !== null)
    );

    return selected.filter((market) => {
      const key = `${market.marketUuid}-${market.takerDivisionUuid}`;
      return !failedMarketKeys.has(key);
    });
  }, [step, selected, submissionErrors]);

  const ratesByMarket = useMemo(
    () => new Map(effectiveRates.map((item) => [item.takerMarket, { rate: item.rate, rateType: item.rateType }])),
    [effectiveRates]
  );

  const stats = useMemo(() => {
    const formatDateRange = (earliest: Date | null, latest: Date | null) => {
      if (!earliest || !latest) {
        return '--';
      }

      const formatDateWithoutYear = (date: Date) => asDateStringText({ date, formatOptions: { year: undefined } });

      return formatDateWithoutYear(earliest) === formatDateWithoutYear(latest)
        ? formatDateWithoutYear(earliest)
        : `${formatDateWithoutYear(earliest)} - ${formatDateWithoutYear(latest)}`;
    };

    const reducedStats = displayedMarkets.reduce<OfferSummaryStats>(
      (acc, curr) => {
        const rateInfo = ratesByMarket.get(curr);

        if (!rateInfo) {
          return acc;
        }

        const { rate, rateType } = rateInfo;

        acc.eligibleInvoiceCount += curr.eligibleInvoiceCount;

        const convertedDpeWeightedSum = convertCurrency({
          amount: curr.eligibleDpeWeightedSum,
          exchangeRates,
          from: curr.currency,
          to: selectedCurrency,
        });
        const convertedInvoiceAmount = convertCurrency({
          amount: curr.eligibleInvoiceAmount,
          exchangeRates,
          from: curr.currency,
          to: selectedCurrency,
        });
        const convertedEligibleDiscountAmount = convertCurrency({
          amount: calculateDiscount({
            dpeWeightedAvg: curr.eligibleDpeWeightedAvg,
            invoiceAmount: curr.eligibleInvoiceAmount,
            isDiscount: rateType === 'DISC',
            rate: rate ?? 0,
          }),
          exchangeRates,
          from: curr.currency,
          to: selectedCurrency,
        });

        acc.eligibleDpeWeightedSum += convertedDpeWeightedSum;
        acc.eligibleInvoiceAmount += convertedInvoiceAmount;
        acc.eligibleDiscountAmount += convertedEligibleDiscountAmount;
        // track unique customers
        acc.customerUuids.add(curr.makerOrganizationUuid);

        // get earliest and latest marketPayDate for calculating range
        if (curr.marketPayDate) {
          const currentPayDate = new Date(curr.marketPayDate);

          if (!acc.earliestPayDate || currentPayDate < acc.earliestPayDate) {
            acc.earliestPayDate = currentPayDate;
          }

          if (!acc.latestPayDate || currentPayDate > acc.latestPayDate) {
            acc.latestPayDate = currentPayDate;
          }
        }

        // get earliest and latest marketGoodFundsDate for calculating range
        if (curr.marketGoodFundsDate) {
          const currentGoodFundsDate = new Date(curr.marketGoodFundsDate);

          if (!acc.earliestGoodFundsDate || currentGoodFundsDate < acc.earliestGoodFundsDate) {
            acc.earliestGoodFundsDate = currentGoodFundsDate;
          }

          if (!acc.latestGoodFundsDate || currentGoodFundsDate > acc.latestGoodFundsDate) {
            acc.latestGoodFundsDate = currentGoodFundsDate;
          }
        }

        return acc;
      },
      {
        avgDaysPaidEarly: 0,
        customerUuids: new Set(),
        earliestPayDate: null,
        earliestGoodFundsDate: null,
        eligibleDpeWeightedSum: 0,
        eligibleInvoiceAmount: 0,
        eligibleInvoiceCount: 0,
        eligibleDiscountAmount: 0,
        latestPayDate: null,
        latestGoodFundsDate: null,
        payDateRange: '--',
        goodFundsDateRange: '--',
      }
    );

    // calculate average days paid early
    reducedStats.avgDaysPaidEarly =
      reducedStats.eligibleInvoiceAmount > 0
        ? reducedStats.eligibleDpeWeightedSum / reducedStats.eligibleInvoiceAmount
        : 0;

    // calculate pay date range string and format string based off min and max pay dates
    reducedStats.payDateRange = formatDateRange(reducedStats.earliestPayDate, reducedStats.latestPayDate);

    // calculate good funds date range string and format string based off min and max good funds dates
    reducedStats.goodFundsDateRange = formatDateRange(
      reducedStats.earliestGoodFundsDate,
      reducedStats.latestGoodFundsDate
    );

    return reducedStats;
  }, [displayedMarkets, ratesByMarket, exchangeRates, selectedCurrency, asDateStringText]);

  // build schedule sentence for offer schedule display
  const scheduleSentence = buildCustomScheduleSentenceFromFormData({
    startDate: schedule.startDate,
    endDate: schedule.endDate,
    cadence: schedule.cadence,
    t,
  });

  return (
    <>
      <EditScheduleModal open={showEditScheduleModal} onClose={() => setShowEditScheduleModal(false)} />
      <EditFrequencyModal open={showEditFrequencyModal} onClose={() => setShowEditFrequencyModal(false)} />
      <div className="rounded-lg">
        {selected.length === 0 ? (
          <div className="flex h-60 flex-col items-center justify-center space-y-6 rounded-lg bg-white p-4">
            <div>
              <img src={c2foComputer} alt="c2fo computer" className="size-14" />
            </div>
            <div className="text-center text-text-secondary">{t('builder.selectACustomerToGetStarted')}</div>
          </div>
        ) : (
          <div className="space-y-4">
            <div
              className={cn('divide-y-[0.5px] divide-stroke rounded-lg border-[0.5px] border-stroke bg-white', {
                'bg-deepGray-50': step === 'confirmation',
              })}
            >
              <div className="flex flex-col justify-between gap-x-6 p-4 md:flex-row md:items-center">
                <div className="text-sm">{t('core.includedAmount')}</div>
                <div className="font-bold md:text-right">
                  {asCurrency(stats.eligibleInvoiceAmount, selectedCurrency)}
                </div>
              </div>
              <div className="flex flex-col justify-between gap-x-6 p-4 md:flex-row md:items-center">
                <div className="text-sm">{t('core.customers')}</div>
                <div className="font-bold md:text-right">{asNumber({ value: stats.customerUuids.size })}</div>
              </div>
              <div className="flex flex-col justify-between gap-x-6 p-4 md:flex-row md:items-center">
                <div className="text-sm">{t('maker.invoices')}</div>
                <div className="font-bold md:text-right">{asNumber({ value: stats.eligibleInvoiceCount })}</div>
              </div>
              <div className="flex flex-col justify-between gap-x-6 p-4 md:flex-row md:items-center">
                <div className="text-sm">{t('core.averageDaysPaidEarly')}</div>
                <div className="font-bold md:text-right">
                  {asNumber({
                    value: handleNaN(stats.avgDaysPaidEarly),
                    decimals: 0,
                  })}
                </div>
              </div>
              {['confirmation', 'preferred-upgrade', 'rate-select', 'review'].includes(step) && (
                <>
                  <div className="space-y-2 p-4">
                    <div className="flex flex-col justify-between gap-x-6 md:flex-row md:items-center">
                      <div className="text-sm">{t('builder.todaysBlendedRate')}</div>
                      <div className="font-bold md:text-right">
                        <BlendedRateDisplay takerMarkets={displayedMarkets} withInfo />
                      </div>
                    </div>
                    {displayedMarkets.length > 0 && allMarketsHaveRates(displayedMarkets, getEffectiveRate) && (
                      <div className="text-xs text-text-secondary">*{t('builder.todaysBlendedRateDescription')}</div>
                    )}
                  </div>
                  <div className="flex flex-col justify-between gap-x-6 p-4 md:flex-row md:items-center">
                    <div className="text-sm">{t('builder.totalDiscount')}</div>
                    <div className="font-bold md:text-right">
                      {allMarketsHaveRates(displayedMarkets, getEffectiveRate)
                        ? asCurrency(
                            stats.eligibleDiscountAmount > 0 ? -Math.abs(stats.eligibleDiscountAmount) : 0,
                            selectedCurrency
                          )
                        : '--'}
                    </div>
                  </div>
                  {['confirmation', 'preferred-upgrade', 'review'].includes(step) ? (
                    <div className="flex flex-col justify-between gap-x-6 p-4 md:flex-row md:items-center">
                      <div className="text-sm">{t('core.estimatedEarlyPaymentAmount')}</div>
                      <div className="font-bold md:text-right">
                        {asCurrency(
                          handleNaN(stats.eligibleInvoiceAmount - stats.eligibleDiscountAmount),
                          selectedCurrency
                        )}
                      </div>
                    </div>
                  ) : null}
                </>
              )}
              <div className="flex flex-col justify-between gap-x-6 p-4 md:flex-row md:items-center">
                <div className="text-sm">{t('core.depositDate')}</div>
                <div className="font-bold md:text-right">{stats.goodFundsDateRange}</div>
              </div>
              {(step === 'preferred-upgrade' || step === 'rate-select' || (step === 'review' && !isDraftVariant)) &&
                preferredRateSelected && (
                  <div className="flex flex-col justify-between gap-x-6 p-4 md:flex-row md:items-center">
                    <div className="text-sm">{t('draftOffer.labels.term')}</div>
                    <div className="font-bold md:text-right">
                      {asDateStringText({
                        date: addDays(new Date(), PREFERRED_TERM_DAYS),
                        formatOptions: { year: undefined },
                      })}
                    </div>
                  </div>
                )}
              {/* Frequency and Schedule Section */}
              {step !== 'customer-select' && !preferredRateSelected && (
                <div className="p-4">
                  <div className="flex flex-col justify-between gap-x-6 md:flex-row md:items-center">
                    <div className="text-sm">{t('builder.frequency')}</div>
                    <div className="flex max-w-[75%] items-center gap-2 md:ml-auto md:text-right">
                      <div className="font-bold">
                        {!frequency ? '--' : frequency === 'oneTime' ? t('builder.oneTime') : t('builder.recurring')}
                      </div>
                    </div>
                  </div>
                  {isDraftVariant && draftOfferProperties?.termExpirationDate && (
                    <div className="mt-2 flex flex-col justify-between gap-x-6 md:flex-row md:items-center">
                      <div className="text-sm">{t('draftOffer.labels.term')}</div>
                      <div className="font-bold md:text-right">
                        {asDateStringText({
                          date: draftOfferProperties.termExpirationDate,
                          formatOptions: { year: undefined },
                        })}
                      </div>
                    </div>
                  )}
                  {enableAutomations &&
                    frequency === 'recurring' &&
                    ['confirmation', 'review'].includes(step) &&
                    !isDraftVariant && (
                      <div className="mt-2 flex flex-col justify-between gap-x-6 rounded-lg bg-canvas p-4 md:flex-row md:items-center">
                        <div className="text-sm">{t('scheduling.schedule')}</div>
                        <div className="flex max-w-[75%] items-center gap-2 md:ml-auto md:text-right">
                          <div className="font-bold">{scheduleSentence}</div>
                          {step === 'review' ? (
                            <IconButton
                              className="h-7 w-7 bg-transparent [&>svg]:h-5 [&>svg]:w-5"
                              disabled={offerSubmitting}
                              icon={PencilIcon}
                              name={t('builder.editScheduleModalTitle')}
                              onClick={() => setShowEditScheduleModal(true)}
                              size="sm"
                              variant="ancillary"
                            />
                          ) : null}
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default OfferSummary;
